// components/checkout/DeliveryOptions.tsx
import React, { useState, useEffect } from 'react';
import { Truck, Package, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { ordersAPI } from '../../api/orders'; // Adjust path as needed
import { formatPrice } from '../../utils/utilityFunctions';
import type { CartItem } from '../../types/cart';
import type { DeliveryOption, DeliveryOptionsProps } from '../../types/delivery';

// interface DeliveryOption {
//     type: 'fastest' | 'economic';
//     fee: number;
//     shipments: number;
//     estimated_days: string;
//     description: string;
//     details?: {
//         warehouse_id: number;
//         warehouse_name: string;
//         items: number;
//         fee: number;
//     }[];
// }

// interface DeliveryOptionsProps {
//     selectedAddressId: number | null;
//     cartItems: any[];
//     onSelect: (option: DeliveryOption) => void;
//     selectedOption?: DeliveryOption | null;
//     onOptionsLoaded?: (options: any[]) => void;
// }

const DeliveryOptions: React.FC<DeliveryOptionsProps> = ({
    selectedAddressId,
    cartItems,
    onSelect,
    selectedOption,
    onOptionsLoaded
}) => {
    const [options, setOptions] = useState<DeliveryOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedOption, setExpandedOption] = useState<string | null>(null);

    useEffect(() => {
        const fetchOptions = async () => {
            if (!selectedAddressId || cartItems.length === 0) return;

            setLoading(true);
            setError(null);

            try {
                // Format items for API using CartItem type
                const items = cartItems.map((item: CartItem) => ({
                    product_variant_id: item.product_variant_id,
                    quantity: item.quantity,
                    price: item.price,
                    product_title: item.product_title,
                    variant_name: item.variant_name,
                    image_url: item.image_url
                }));

                const response = await ordersAPI.getDeliveryOptions({
                    address_id: selectedAddressId,
                    items
                });

                if (!response.error && response.data?.options) {
                    setOptions(response.data.options);
                    if (onOptionsLoaded) {
                        onOptionsLoaded(response.data.options);
                    }
                } else {
                    setError(response.error || 'Failed to load options');
                }
            } catch (err) {
                setError('Failed to fetch delivery options');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchOptions();
    }, [selectedAddressId, cartItems]);

    if (!selectedAddressId) {
        return (
            <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                <Truck className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>Select an address to see delivery options</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="space-y-3">
                {[1, 2].map(i => (
                    <div key={i} className="border rounded-lg p-4 animate-pulse">
                        <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 rounded-lg p-4 text-center text-red-600">
                <p>{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="mt-2 text-sm underline"
                >
                    Try again
                </button>
            </div>
        );
    }

    if (options.length === 0) {
        return (
            <div className="bg-yellow-50 rounded-lg p-6 text-center text-gray-600">
                <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>No delivery options available for these items</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Delivery Options</h3>

            {options.map(option =>{
            return (
                <div
                    key={option.type}
                    className={`border rounded-lg transition-all ${selectedOption?.type === option.type
                        ? 'border-primary-500 ring-1 ring-primary-500'
                        : 'hover:border-gray-300'
                        }`}
                >
                    {/* Main option row - click to select */}
                    <div
                        className="p-4 cursor-pointer"
                        onClick={() => onSelect(option)}
                    >
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        name="delivery"
                                        checked={selectedOption?.type === option.type}
                                        onChange={() => onSelect(option)}
                                        className="h-4 w-4 text-primary-600"
                                    />
                                    <span className="font-medium capitalize">{option.type}</span>
                                    {option.shipments > 1 && (
                                        <span className="inline-flex items-center text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full whitespace-nowrap sm:whitespace-normal">
                                            <Package className="w-3 h-3 mr-1 flex-shrink-0" />
                                            <span>{option.shipments} packages</span>
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600 mt-1 ml-6">
                                    {option.description}
                                </p>
                            </div>

                            <div className="text-right ml-4">
                                <div className="font-bold text-lg">{formatPrice(option.fee)}</div>
                                <div className="text-xs text-gray-500 flex items-center justify-end">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {option.estimated_days}
                                </div>
                            </div>
                        </div>

                        {/* Expand/collapse toggle for details */}
                        {option.details && option.details.length > 1 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedOption(expandedOption === option.type ? null : option.type);
                                }}
                                className="mt-2 ml-6 text-xs text-gray-500 flex items-center hover:text-gray-700"
                            >
                                {expandedOption === option.type ? (
                                    <>Hide details <ChevronUp className="h-3 w-3 ml-1" /></>
                                ) : (
                                    <>Show shipping breakdown <ChevronDown className="h-3 w-3 ml-1" /></>
                                )}
                            </button>
                        )}
                    </div>

                    {/* Expanded details */}
                    {expandedOption === option.type && option.details && (
                        <div className="px-4 pb-4 pt-2 border-t bg-gray-50">
                            <p className="text-xs font-medium text-gray-500 mb-2">SHIPPING FROM:</p>
                            {option.details.map((shipment, idx) => (
                                <div key={idx} className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600">
                                        <Package className="h-3 w-3 inline mr-1" />
                                        {shipment.warehouse_name}
                                        <span className="text-xs text-gray-400 ml-1">
                                            ({shipment.items?.length} items)
                                        </span>
                                    </span>
                                    <span className="font-medium">${shipment.fee}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )})}
        </div>
    );
};

export default DeliveryOptions;